import {
  Input,
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";

import { AppStore } from "../../../../stores/app-store.service";
import { Subscription } from "rxjs";

import { FilterStatus } from "../../../../models/filters";
import { writeColorStatusSelection } from "../../../../models/color-status-filter";
import {
  IncidentAssociatedDeviceDTO,
  IncidentDTO
} from "../../../../models/incident";
import { SiteDTO } from "../../../../models/site";
import { MapFilter } from "../../../../models/map-filter";
import { IncidentControllerService } from "../../../../services/incident-controller.service";
import {
  filterTornOptions,
  filterUnknownOptions
} from "../../../../services/device-container-state-controller.service";
import { Range } from "../../../../models/range-status-days";
import { countActiveFilters } from "../../../../models/active-filters-count";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIcon } from "@angular/material/icon";
import { SearchbarListComponent } from "../../../elements/searchbar-list/search-site-list.component";
import { FilterControlComponent } from "../../../elements/filter-control/filter-control.component";
import { TranslateModule } from "@ngx-translate/core";
import { FilterKey } from "../../../../services/filters.service";

@Component({
  selector: "app-header-map",
  templateUrl: "./header-map.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./header-map.component.scss"],
  imports: [
    MatTooltip,
    MatIcon,
    SearchbarListComponent,
    FilterControlComponent,
    TranslateModule
  ]
})
export class HeaderMapComponent implements OnInit, OnChanges, OnDestroy {
  @Output() torn: EventEmitter<string> = new EventEmitter();
  @Output() unknown: EventEmitter<string> = new EventEmitter();
  @Output() devices: EventEmitter<string[]> = new EventEmitter();
  @Output() lastSites: EventEmitter<string[]> = new EventEmitter();
  @Output() incidents: EventEmitter<FilterKey[]> = new EventEmitter();
  @Output() containers: EventEmitter<string[]> = new EventEmitter();
  @Output() filter: EventEmitter<FilterStatus> = new EventEmitter();
  @Output() colorStatus: EventEmitter<FilterStatus[]> = new EventEmitter();
  @Output() statusDays: EventEmitter<Range> = new EventEmitter();
  @Output() site: EventEmitter<string | undefined> = new EventEmitter();
  @Output() confidenceRadius: EventEmitter<string[]> = new EventEmitter();
  @Output() devicesTypes: EventEmitter<string[]> = new EventEmitter();
  @Output() addIncidentDevices: EventEmitter<number[]> = new EventEmitter();
  @Output() toggleFilters: EventEmitter<boolean> = new EventEmitter();

  @Input() loadingProgress: number;
  @Input() listSites: SiteDTO[];
  @Input() isEmptyIncidentDevice: boolean;
  @Input() listIncidentDevices: IncidentAssociatedDeviceDTO[];

  @Input() filterTorn: string;
  @Input() filterUnknown: string;
  @Input() filterStatusDays: Range;
  @Input() filterDevices: string[] = [];
  @Input() filterLastSites: string[] = [];
  @Input() filterIncidents: FilterKey[] = [];
  @Input() filterContainers: string[] = [];
  @Input() filterConfidenceRadius: string[];
  @Input() filterDevicesTypes: string[];
  @Input() filterContainerCategory: string[] = [];
  @Input() filterSite: string | undefined = undefined;
  @Input() isSidenavOpen: boolean = false;

  public index: number = 20;
  public isLoading: boolean = false;
  public searchFocus: boolean = false;
  public activeSite: SiteDTO = undefined;
  public statusList: FilterStatus[] = [];

  public option: number;
  public tornFilterOptions: string[] = Object.keys(filterTornOptions);
  public unknownFilterOptions: string[] = Object.keys(filterUnknownOptions);

  private subscription: Subscription = new Subscription();

  @ViewChild("selectionCheckbox", { static: true })
  public selectionCheckbox: MatCheckbox;

  constructor(
    private store: AppStore,
    private incidentService: IncidentControllerService
  ) {}

  public ngOnInit(): void {
    this.initStore();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.listSites || changes.filterSite) {
      if (
        this.filterSite &&
        this?.listSites?.length &&
        this.activeSite?.code !== this.filterSite
      ) {
        this.activeSite = this.listSites.find(
          (site) => site.code === this.filterSite
        );
      }
    }
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private initStore(): void {
    this.subscription.add(
      this.store.statusStore$.subscribe((statusData: FilterStatus[]) => {
        this.statusList = statusData;
        writeColorStatusSelection(statusData);
      })
    );
  }

  /**
   * The picked site, emitted for the page to navigate to. `mapFilter` (the camera)
   * is still remembered, but the site itself is not: it is the
   * `/map/site/:site_code` segment, and the URL is the only place it is written.
   */
  public siteChanged(): void {
    this.activeSite = this.listSites.find(
      (site) => site.code === this.filterSite
    );
    if (this.activeSite) {
      let mapfilter: MapFilter = {
        centerLat: this.activeSite.latitude,
        centerLng: this.activeSite.longitude,
        zoomLevel: 15
      };
      this.saveFilter(mapfilter);
      this.site.emit(this.activeSite.code);
    }
  }

  public resetSite(): void {
    this.activeSite = undefined;
    this.site.emit(undefined);
  }

  public manageFilterSite(siteCode: string): void {
    this.filterSite = siteCode;

    if (!siteCode) {
      this.resetSite();
    } else {
      this.siteChanged();
    }
  }

  public saveFilter(mapfilter: MapFilter): void {
    localStorage.setItem("mapFilter", JSON.stringify(mapfilter));
  }

  public manageContainers(containers: string[]): void {
    this.containers.emit(containers);
  }

  public manageDevice(devices: string[]): void {
    this.devices.emit(devices);
  }

  public manageStatusDays(statusDays: Range): void {
    this.statusDays.emit(statusDays);
  }

  public manageConfidenceRadius(radius: string[]): void {
    this.confidenceRadius.emit(radius);
  }

  public manageDevicesTypes(devicesTypes: string[]): void {
    this.devicesTypes.emit(devicesTypes);
  }

  public manageLAS(sites: string[]): void {
    this.lastSites.emit(sites);
  }

  public manageFilters(filter): void {
    this.filter.emit(filter);
  }

  public manageColorStatus(colors: FilterStatus[]): void {
    this.colorStatus.emit(colors);
  }

  public manageIncidents(incidents: FilterKey[]): void {
    this.incidents.emit(incidents);
  }

  public manageTorn(torn: string): void {
    this.torn.emit(torn);
  }

  public manageUnknown(unknown: string): void {
    this.unknown.emit(unknown);
  }

  public addIncident(incidentsIds: FilterKey[]): void {
    if (!incidentsIds?.length) {
      this.addIncidentDevices.emit();
    } else {
      this.isLoading = true;

      this.subscription.add(
        this.incidentService
          .getAllIncidentsByIncidentID(incidentsIds)
          .subscribe((incidents: IncidentDTO[]) => {
            let devices: Partial<IncidentAssociatedDeviceDTO>[] = [];
            incidents.forEach((incident) => {
              this.listIncidentDevices.forEach((device) => {
                devices.push({
                  isOpened: false,
                  incidentId: incident.id,
                  deviceId: device.deviceId,
                  lastAuthorizedSite: incident.lastAuthorizedSite,
                  containerType: incident.containerType,
                  assignedBy: incident.createdBy,
                  closingDate: null
                });
              });
            });
            this.incidentService
              .postSelectDevices(devices)
              .subscribe((_res) => {
                incidents.forEach((incident) => {
                  if (!incident.currentSite) incident.currentSite = undefined;
                  this.incidentService
                    .getCountDevices(incident.id)
                    .subscribe((res) => {
                      incident.totalQuantity = res.count;
                      this.subscription.add(
                        this.incidentService.patchIncident(incident).subscribe()
                      );
                    });
                });
                this.isLoading = false;
              });
          })
      );
    }
  }

  public onToggleFilters(): void {
    this.toggleFilters.emit(!this.isSidenavOpen);
  }

  public get activeFiltersCount(): number {
    return countActiveFilters({
      containers: this.filterContainers,
      containerCategory: this.filterContainerCategory,
      incidents: this.filterIncidents,
      lastSites: this.filterLastSites,
      devices: this.filterDevices,
      confidenceRadius: this.filterConfidenceRadius,
      devicesTypes: this.filterDevicesTypes,
      statusDays: this.filterStatusDays,
      colorStatus: this.statusList
    });
  }
}
